import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, ShieldCheck, UserRound, KeyRound, Smartphone, Crown, Users, ArrowRight, MessagesSquare } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { mockData } from '../mock';
import { AvatarUploader } from '../components/member/AvatarUploader';
import { ProfileForm } from '../components/member/ProfileForm';
import { PasswordForm } from '../components/member/PasswordForm';
import PasskeyManager from '../components/member/PasskeyManager';
import AuthenticatorManager from '../components/member/AuthenticatorManager';
import { MemberCard } from '../components/member/MemberCard';
import { FavoriteVideos } from '../components/member/FavoriteVideos';
import { Seo } from '../components/Seo';
import { useT } from '../context/LanguageContext';

const Card = ({ icon: Icon, title, children, testId }) => (
  <div data-testid={testId} className="rounded-3xl bg-[#0f0a17]/90 backdrop-blur-xl border border-white/10 p-7 md:p-9">
    <div className="flex items-center gap-3 mb-6">
      <div className="w-10 h-10 rounded-full bg-[#ff2e2e]/15 text-[#ff2e2e] flex items-center justify-center"><Icon size={20} /></div>
      <h2 className="text-lg md:text-xl font-bold text-white">{title}</h2>
    </div>
    {children}
  </div>
);

const Member = () => {
  const { user, setUser, logout } = useAuth();
  const { t, lang } = useT();
  const navigate = useNavigate();

  useEffect(() => {
    if (user === false) navigate('/login', { replace: true });
  }, [user, navigate]);

  if (!user) {
    return <div className="min-h-screen bg-black flex items-center justify-center text-white/60" data-testid="member-loading">{t('Lastar…')}</div>;
  }

  const d = user.created_at ? new Date(user.created_at) : null;
  const locales = { nn: 'nn-NO', nb: 'nb-NO', en: 'en-GB', pl: 'pl-PL', de: 'de-DE', es: 'es-ES' };
  const memberSince = d ? new Intl.DateTimeFormat(locales[lang] || 'nn-NO', { day: 'numeric', month: 'long', year: 'numeric' }).format(d) : '';

  return (
    <div className="bg-black min-h-screen text-white" data-testid="member-page">
      <Seo title={`${t('Medlemsside')} | Refuel Weekend`} description={t('Di personlege medlemsside på Refuel Weekend.')} path="/medlem" noindex />
      <Navbar />
      <section className="relative overflow-hidden">
        <div className="absolute inset-0" style={{ backgroundImage: `url(${mockData.heroImage})`, backgroundSize: 'cover', backgroundPosition: 'center', filter: 'blur(6px)', transform: 'scale(1.05)' }} />
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/85 to-black" />
        <div className="relative max-w-[1200px] mx-auto px-4 md:px-8 pt-16 pb-12 flex flex-col md:flex-row items-center md:items-end gap-8">
          <AvatarUploader user={user} onUpdated={setUser} />
          <div className="flex-1 text-center md:text-left">
            <div className="flex items-center justify-center md:justify-start gap-2 mb-2">
              <p className="text-[#ff2e2e] font-bold uppercase tracking-widest text-sm">{t('Medlemsside')}</p>
              {user.is_admin && (
                <span data-testid="admin-badge" className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#ffb400] text-black text-[10px] font-bold uppercase tracking-widest"><Crown size={12} /> {t('Admin')}</span>
              )}
            </div>
            <h1 data-testid="member-name" className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight" style={{ fontFamily: "'Oktah Round', sans-serif" }}>
              {t('Hei, {name}!').replace('{name}', user.name)}
            </h1>
            <p data-testid="member-email" className="text-white/60 mt-2">{user.email}</p>
            {memberSince && <p className="text-white/40 text-sm mt-1">{t('Medlem sidan')} {memberSince}</p>}
          </div>
          <button onClick={async () => { await logout(); navigate('/'); }} data-testid="member-logout-button" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/20 text-white/80 hover:border-[#ff2e2e] hover:text-[#ff2e2e] transition-colors text-sm font-bold">
            <LogOut size={16} /> {t('Logg ut')}
          </button>
        </div>
      </section>

      <main className="max-w-[1200px] mx-auto px-4 md:px-8 pb-24 grid grid-cols-1 md:grid-cols-2 gap-6">
        <Link to="/medlemssenter" data-testid="member-center-link" className="md:col-span-2 group flex items-center gap-4 rounded-xl border border-[#ff2e2e]/25 bg-[#ff2e2e]/5 p-5 transition hover:bg-[#ff2e2e]/10">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-[#ff2e2e]/15 text-[#ff5555]"><MessagesSquare size={20} /></div>
          <div className="min-w-0 flex-1"><p className="font-bold text-white">Medlemssenter</p><p className="mt-0.5 text-sm text-white/55">Chatter, medlemsgrupper og meir.</p></div>
          <ArrowRight size={19} className="shrink-0 text-[#ff5c5c] transition-transform group-hover:translate-x-1" />
        </Link>
        <div className="md:col-span-2"><MemberCard user={user} /></div>
        {user.is_admin && (
          <Link to="/admin" data-testid="admin-members-link" className="md:col-span-2 group rounded-3xl border border-[#ffb400]/40 bg-[#ffb400]/5 p-6 md:p-7 flex items-center gap-4 hover:bg-[#ffb400]/10 transition-colors">
            <div className="w-12 h-12 rounded-full bg-[#ffb400]/20 text-[#ffb400] flex items-center justify-center shrink-0"><Users size={22} /></div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-white text-lg">{t('Adminpanel')}</p>
              <p className="text-white/60 text-sm">{t('Medlemsoversikt, nyheitsbrev, videoar og anmeldingar – alt på éin stad.')}</p>
            </div>
            <ArrowRight size={22} className="text-[#ffb400] group-hover:translate-x-1 transition-transform shrink-0" />
          </Link>
        )}
        <Card icon={UserRound} title="Profil" testId="profile-card">
          <ProfileForm user={user} onUpdated={setUser} />
        </Card>
        <Card icon={KeyRound} title={t('Passnøkler')} testId="passkey-card">
          <PasskeyManager user={user} onUpdated={setUser} />
        </Card>
        <Card icon={Smartphone} title={t('Authenticator-app')} testId="authenticator-card">
          <AuthenticatorManager user={user} onUpdated={setUser} />
        </Card>
        {user.is_admin ? (
          <Card icon={ShieldCheck} title={t('Admininnlogging')} testId="admin-password-card">
            <div className="mb-5 rounded-xl border border-[#ffb400]/30 bg-[#ffb400]/5 px-4 py-3">
              <p className="text-white/50 text-[10px] uppercase tracking-widest font-bold">{t('Adminnummer')}</p>
              <p data-testid="admin-number" className="text-[#ffb400] text-xl font-bold tracking-wider mt-1">{user.admin_number || t('Tildeles ved innlasting…')}</p>
            </div>
            <PasswordForm user={user} onUpdated={setUser} />
          </Card>
        ) : (
          <Card icon={KeyRound} title={t(user.has_password ? 'Byt passord' : 'Legg til passord')} testId="password-card">
            <PasswordForm user={user} onUpdated={setUser} />
          </Card>
        )}
        <div className="md:col-span-2"><FavoriteVideos /></div>
        <div data-testid="member-status-card" className="md:col-span-2 rounded-3xl border border-[#ff2e2e]/30 bg-[#ff2e2e]/5 p-7 flex items-start gap-4">
          <ShieldCheck className="text-[#ff2e2e] shrink-0" size={28} />
          <div>
            <p className="font-bold text-white">{t('Du er klar for Refuel Weekend 27')}</p>
            <p className="text-white/60 text-sm mt-1">{t('Innlogging via')} {t(user.auth_provider === 'google' ? 'Google' : user.auth_provider === 'both' ? 'Google og e-post' : user.auth_provider === 'magic' ? 'e-postlenke' : 'e-post og passord')}.</p>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Member;
